import { NotificationItem, INotificationSummary } from 'typings/notificationSummary';
import { dataManagerFactory } from 'utils/MobxDataManager/singletons';
import { compareItem } from 'common/compareItem';
import { action, observable, computed, makeObservable } from 'mobx';
import { NotificationName } from 'typings/enums';
import { NotificationItemWorkflowSendReview } from 'nomst/Stores/Notification/notificationItemWorkflowSendReview';
import { NotificationItemWorkflowSendBack } from 'nomst/Stores/Notification/notificationItemWorkflowSendBack';
import { NotificationItemPromotionRequestForReview } from 'nomst/Stores/Notification/notificationItemPromotionRequestForReview';
import { NotificationItemPromotionRequestUpdated } from 'nomst/Stores/Notification/notificationItemPromotionRequestUpdated';
import { NotificationItemPromotionRequestComment } from 'nomst/Stores/Notification/notificationItemPromotionRequestComment';
import { NotificationItemConversationAdded } from 'nomst/Stores/Notification/notificationItemConversationAdded';
import { NotificationItemWorkflowApproved } from 'nomst/Stores/Notification/notificationItemWorkflowApproved';

export class NotificationStore {
    @observable count: number;
    @observable notificationItems: NotificationItem[];
    @observable lastFetchDate: string;
    @observable lastReadDate: Date;
    @observable visible: boolean;
    @observable isLoading: boolean;
    masterAccountId: string;
    @observable cycleId: number;

    constructor(maid: string) {
        makeObservable(this);
        this.count = 0;
        this.masterAccountId = maid;
        this.notificationItems = [];
        this.cycleId = 0;
    }

    @action
    setCycleId(id: number): void {
        this.cycleId = id;
    }

    @computed
    get sortedNotificationItems(): NotificationItem[] {
        if (!this.notificationItems?.length) {
            return [];
        }

        return this.notificationItems.slice().sort((item1: NotificationItem, item2: NotificationItem) =>
            compareItem(item1.timeStampDateType, item2.timeStampDateType, false));
    }

    @computed
    get hasNotificationItems(): boolean {
        return this.notificationItems?.length > 0;
    }

    @action
    resetCount(): void {
        this.count = 0;
    }

    @action
    async setLastReadNotifications(): Promise<void> {
        if (!this.masterAccountId || this.cycleId === 0 ||
            this.isLoading || this.lastReadDate) {
            return Promise.resolve();
        }

        await dataManagerFactory.getNotificationDataManager().setLastReadNotifications(this.masterAccountId, this.lastFetchDate);
        this.lastReadDate = new Date();
        setTimeout(() => {
            this.resetCount();
        }, 2000);
    }

    @action
    async fetchNotificationSummary(): Promise<void> {
        if (!this.masterAccountId || this.cycleId === 0) {
            return Promise.resolve();
        }

        this.isLoading = true;
        let notificationSummary: Nullable<INotificationSummary> =
            await dataManagerFactory.getNotificationDataManager().getNotificationSummary(this.masterAccountId, this.cycleId);
        notificationSummary = notificationSummary || {} as INotificationSummary;
        this.count = notificationSummary.count;
        this.notificationItems = notificationSummary.notifications?.map(item => {
            return this.initCorrespondingNotificationItem(item);
        });
        this.lastFetchDate = notificationSummary.timestamp;
        this.isLoading = false;

        if (this.visible) {
            await this.setLastReadNotifications();
        }
    }

    initCorrespondingNotificationItem(item: IBaseNotificationItem): NotificationItem {
        const name = item.notificationName as NotificationName;
        switch (name) {
            case NotificationName.NotificationDataWorkflowSendReview:
                return new NotificationItemWorkflowSendReview(item);
            case NotificationName.NotificationDataWorkflowSendBack:
                return new NotificationItemWorkflowSendBack(item);
            case NotificationName.NotificationDataPromotionRequestForReview:
                return new NotificationItemPromotionRequestForReview(item);
            case NotificationName.NotificationDataPromotionRequestUpdated:
                return new NotificationItemPromotionRequestUpdated(item);
            case NotificationName.NotificationDataPromotionRequestComment:
                return new NotificationItemPromotionRequestComment(item);
            case NotificationName.NotificationDataConversationAdded:
                return new NotificationItemConversationAdded(item);
            case NotificationName.NotificationDataWorkflowApproved:
                return new NotificationItemWorkflowApproved(item);
        }
    }

    @action
    toggleNotificationMenu = () => {
        this.visible = !this.visible;
    }

    @action
    hideNotificationMenu = () => {
        this.visible = false;
    }
}